import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import type { ReactNode } from 'react'
import { api, clearTokens, loadTokens, request, saveTokens } from '../api/client'
import type { PortalLoginResponse, PortalUser } from '../api/types'

export interface WorkspaceChoice {
  slug: string
  name: string
}

interface PortalAuthState {
  user: PortalUser | null
  loading: boolean
  /** Signs in; or, when the password works with several transporters, returns them to choose from. */
  login: (email: string, password: string, tenantSlug?: string) => Promise<WorkspaceChoice[] | null>
  logout: () => void
}

const PortalAuthContext = createContext<PortalAuthState | null>(null)

const USER_KEY = 'fleey.portal.user'

export function PortalAuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<PortalUser | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const tokens = loadTokens()
    const cached = localStorage.getItem(USER_KEY)

    if (!tokens || !cached) {
      setLoading(false)
      return
    }

    // Render from cache immediately, then confirm the session still stands. The
    // dashboard is the cheapest authenticated call the portal has, and it is
    // the first thing the customer sees anyway.
    setUser(JSON.parse(cached) as PortalUser)

    api
      .get('/portal/dashboard')
      .catch(() => {
        clearTokens()
        localStorage.removeItem(USER_KEY)
        setUser(null)
      })
      .finally(() => setLoading(false))
  }, [])

  const login = useCallback(async (email: string, password: string, tenantSlug?: string) => {
    const response = await request<{ data: PortalLoginResponse & { status?: string; workspaces?: WorkspaceChoice[] } }>('/portal/auth/login', {
      method: 'POST',
      body: { email, password, ...(tenantSlug ? { tenant_slug: tenantSlug } : {}) },
      retryOn401: false,
    })

    if (response.data.status === 'choose_workspace') {
      return response.data.workspaces ?? []
    }

    saveTokens({
      access: response.data.access_token,
      refresh: response.data.refresh_token,
    })

    localStorage.setItem(USER_KEY, JSON.stringify(response.data.user))
    setUser(response.data.user)
    return null
  }, [])

  const logout = useCallback(() => {
    const tokens = loadTokens()

    if (tokens?.refresh) {
      // Fire and forget: the local session ends whatever the server replies.
      void request('/auth/logout', {
        method: 'POST',
        body: { refresh_token: tokens.refresh },
      }).catch(() => undefined)
    }

    clearTokens()
    localStorage.removeItem(USER_KEY)
    setUser(null)
  }, [])

  const value = useMemo(() => ({ user, loading, login, logout }), [user, loading, login, logout])

  return <PortalAuthContext.Provider value={value}>{children}</PortalAuthContext.Provider>
}

export function usePortalAuth(): PortalAuthState {
  const context = useContext(PortalAuthContext)
  if (!context) throw new Error('usePortalAuth must be used inside PortalAuthProvider')
  return context
}
